import React, { useState, useEffect, useRef, useContext } from 'react';
import axios from 'axios';
import '../../../assets/css/patient/blogpost.css';
import MyNavbar from '../../../components/general/navbar/MyNavbar';
import Footer from '../../../components/general/footer/Footer';
import { AppContext } from '../../../context/AppContext';

const getInitials = (name = '') =>
  name.split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase();

/* ---------- inline icons (no external icon dependency) ---------- */
const Icon = {
  Calendar: (p) => (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" {...p}>
      <rect x="4" y="5.5" width="16" height="14.5" rx="2" />
      <path d="M8 3.5v4M16 3.5v4M4 10h16" strokeLinecap="round" />
    </svg>
  ),
  Clock: (p) => (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" {...p}>
      <circle cx="12" cy="12" r="8.2" />
      <path d="M12 7.5V12l3 2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Arrow: (p) => (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" {...p}>
      <path d="M13 6l6 6-6 6M5 12h14" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Sparkle: (p) => (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor" {...p}>
      <path d="M12 2.5c.4 3.3 1.1 5.4 2.2 6.5s3.2 1.8 6.5 2.2c-3.3.4-5.4 1.1-6.5 2.2s-1.8 3.2-2.2 6.5c-.4-3.3-1.1-5.4-2.2-6.5S6.6 11.6 3.3 11.2c3.3-.4 5.4-1.1 6.5-2.2S11.6 5.8 12 2.5Z" />
    </svg>
  ),
};

export default function BlogDetail({ postId, onBack, onSelectPost }) {
  const { baseUrl } = useContext(AppContext);

  const [post, setPost]       = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const [readProgress, setReadProgress] = useState(0);
  const articleRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    window.scrollTo({ top: 0, behavior: 'instant' });

    axios
      .get(`${baseUrl}/Blogs/${postId}`)
      .then((res) => {
        setPost(res.data.post);
        setRelated(res.data.related || []);
      })
      .catch((err) => {
        console.error('Blog detail fetch error:', err);
        setError('Could not load this article.');
      })
      .finally(() => setLoading(false));
  }, [baseUrl, postId]);

  useEffect(() => {
    const handleScroll = () => {
      if (!articleRef.current) return;
      const el    = articleRef.current;
      const top   = el.getBoundingClientRect().top;
      const total = el.offsetHeight - window.innerHeight;
      const pct   = Math.min(100, Math.max(0, (-top / total) * 100));
      setReadProgress(pct);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const goToPost = (id) => {
    if (onSelectPost) {
      onSelectPost(id);
    }
  };

  if (loading) {
    return (
      <>
        <MyNavbar />
        <div className="bd-page">
          <div className="bd-state-screen">
            <div className="bd-spinner" aria-hidden="true" />
            <p>Loading article…</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !post) {
    return (
      <>
        <MyNavbar />
        <div className="bd-page">
          <div className="bd-state-screen">
            <p>{error || 'This article could not be found.'}</p>
            <button className="bd-back-btn" onClick={onBack}>
              <Icon.Arrow style={{ transform: 'rotate(180deg)' }} />
              Back to blog
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const paragraphs = (post.content || '').split(/\n\s*\n/).filter(Boolean);
  const dateStr = (post.published_at || post.created_at || '').toString().split('T')[0];
  const hasImage = !!(post.thumbnail_url || post.thumbnail);
  const hasSidebar = related.length > 0;

  // Split the article body into a lead intro and a series of numbered
  // insight blocks, so a numbered list in the source content renders as
  // distinct visual cards instead of flat, undifferentiated paragraphs.
  const intro = [];
  const sections = [];
  let current = null;
  paragraphs.forEach((raw) => {
    const para = raw.replace(/\s+/g, ' ').trim();
    const m = para.match(/^(\d{1,2})[.)]\s*(.+)$/);
    if (m && m[2].length < 80) {
      current = { num: m[1], heading: m[2], body: [] };
      sections.push(current);
    } else if (current) {
      current.body.push(para);
    } else {
      intro.push(para);
    }
  });

  return (
    <>
      <MyNavbar />
      <div className="bd-page">

        {/* ═══════ HERO ═══════ */}
        <section className="bd-hero">
          <div className="bd-hero-bg" />
          <div className="bd-hero-inner">
            <div className="bd-hero-badges">
              <span className="bd-cat-badge">{post.category}</span>
              {post.featured && (
                <span className="bd-featured-badge">
                  <Icon.Sparkle /> Featured
                </span>
              )}
            </div>

            <h1 className="bd-hero-title">{post.title}</h1>
            {post.excerpt && <p className="bd-hero-excerpt">{post.excerpt}</p>}
          </div>
        </section>

        {/* Floating meta card, overlapping the hero/image boundary */}
        <div className={`bd-hero-media ${hasImage ? 'has-image' : 'no-image'}`}>
          {hasImage && (
            <div className="bd-hero-img-wrap">
              <img className="bd-hero-img" src={post.thumbnail_url || post.thumbnail} alt={post.title} />
            </div>
          )}
          <div className="bd-hero-meta-card">
            <span className="bd-meta-divider" />
            <span className="bd-meta-item"><Icon.Calendar /> {dateStr}</span>
            <span className="bd-meta-divider" />
            <span className="bd-meta-item"><Icon.Clock /> {post.read_time} min read</span>
          </div>
        </div>

        <div className={`bd-layout ${hasSidebar ? '' : 'bd-layout-full'}`} ref={articleRef}>
          <article className="bd-article">
            <div className="bd-content-card">
              {intro.map((para, i) => (
                <p key={`intro-${i}`} className={`bd-body-text${i === 0 ? ' bd-lead' : ''}`}>{para}</p>
              ))}

              {sections.length > 0 && (
                <div className="bd-insights">
                  {sections.map((s, idx) => (
                    <div className="bd-insight" key={idx}>
                      <span className="bd-insight-num" aria-hidden="true">{s.num}</span>
                      <div className="bd-insight-body">
                        <h3 className="bd-insight-heading">{s.heading}</h3>
                        {s.body.map((p, i) => (
                          <p key={i} className="bd-body-text">{p}</p>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {post.tags && post.tags.length > 0 && (
              <div className="bd-tags-row">
                <span className="bd-tags-label">Tags</span>
                {post.tags.map((tag, i) => (
                  <span key={i} className="bd-tag">
                    {typeof tag === 'string' ? tag : tag.name}
                  </span>
                ))}
              </div>
            )}
          </article>

          {/* ────────── SIDEBAR (only shown when there's something to show) ────────── */}
          {hasSidebar && (
            <aside className="bd-sidebar">
              <div className="bd-sidebar-card">
                <h3 className="bd-sidebar-title">Related articles</h3>
                <div className="bd-related-list">
                  {related.map((rel) => (
                    <div
                      key={rel.id}
                      className="bd-related-item"
                      onClick={() => goToPost(rel.id)}
                    >
                      <img className="bd-related-img" src={rel.thumbnail_url || rel.thumbnail} alt={rel.title} />
                      <div className="bd-related-info">
                        <p className="bd-related-cat">{rel.category}</p>
                        <p className="bd-related-title">{rel.title}</p>
                        <p className="bd-related-date">
                          {(rel.published_at || rel.created_at || '').toString().split('T')[0]}
                          <span className="bd-related-date-sep" />
                          {rel.read_time} min read
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </aside>
          )}
        </div>

        {/* ═══════ MORE ARTICLES ═══════ */}
        <section className="bd-more-section">
          <div className="bd-more-inner">
            <div className="bd-more-header-row">
              <h2 className="bd-more-title">More articles for you</h2>
              <button className="bd-back-btn" onClick={onBack}>
                <Icon.Arrow style={{ transform: 'rotate(180deg)' }} />
                Back to blog
              </button>
            </div>

            {related.length > 0 ? (
              <div className="bd-more-grid">
                {related.map((rel) => (
                  <div key={rel.id} className="bd-more-card" onClick={() => goToPost(rel.id)}>
                    <div className="bd-more-card-img">
                      <img src={rel.thumbnail_url || rel.thumbnail} alt={rel.title} />
                      <span className="bd-more-card-cat">{rel.category}</span>
                    </div>
                    <div className="bd-more-card-body">
                      <h3 className="bd-more-card-title">{rel.title}</h3>
                      <p className="bd-more-card-excerpt">{rel.excerpt}</p>
                      <div className="bd-more-card-footer">
                        <span className="bd-more-card-meta">
                          {(rel.published_at || rel.created_at || '').toString().split('T')[0]}
                          <span className="bd-related-date-sep" />
                          {rel.read_time} min read
                        </span>
                        <button
                          className="bd-more-card-link"
                          onClick={(e) => { e.stopPropagation(); goToPost(rel.id); }}
                        >
                          Read <Icon.Arrow />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bd-more-empty">
                <p>No related articles yet for this category.</p>
                <button className="bd-back-btn" onClick={onBack}>Browse all articles</button>
              </div>
            )}
          </div>
        </section>

      </div>
      <Footer />
    </>
  );
}